import { computed, ref, toValue, watch } from 'vue';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { useStatementsStore, useStatementReservationsStore, useStatementTransfersStore } from '@/stores/statements';
import {
  FundConfirmationPollInterval,
  StatementStatus,
  StatementStatusTransitionEvents,
  StatementFileStatus,
} from '@/constants/statements.js';
import { useSearchAwareParams } from '@/composables/useSearchAwareParams.js';
import { sanitizeListParams } from '@/utils/query-params.js';
import { QueryGcTime } from '@/constants/http';

import api from './api';

export const useStatements = () => {
  const { filters } = useStatementsStore();
  const searchParams = useSearchAwareParams({
    searchTerm: () => filters.search_text,
    defaultParams: filters,
    transformParams: {
      sort_by: 'relevance',
      sort_order: 'desc',
    },
  });

  return useQuery({
    queryKey: ['statements', searchParams],
    queryFn: () => api.getStatements(sanitizeListParams(searchParams.value)),
  });
};

export const useSearchStatements = (selectFromResult = (item) => item) => {
  const queryClient = useQueryClient();
  return async ({ search = '' }) => {
    const searchParams = {
      search_text: search,
      per_page: 60,
    };

    try {
      const data = await queryClient.fetchQuery({
        queryKey: ['statements', searchParams],
        queryFn: () => api.getStatements(searchParams),
      });
      return data.data.map(selectFromResult);
    } catch {
      return [];
    }
  };
};

export const useStatementById = (id, options = { gcTime: QueryGcTime }) => {
  return useQuery({
    queryKey: ['statement', id],
    queryFn: () => api.getStatementById(toValue(id)),
    gcTime: options.gcTime ?? QueryGcTime,
    enabled: computed(() => !!id.value),
  });
};

export const usePollStatementUntilFunded = (id, enabled = false) => {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['statement', id],
    queryFn: () => api.getStatementById(toValue(id)),
    enabled,
    retry: 0,
    refetchInterval: ({ state }) => {
      const currentStatus = state?.data?.data?.status;
      return currentStatus !== StatementStatus.FUNDED ? FundConfirmationPollInterval : false;
    },
  });

  watch(
    () => query.data.value?.data?.status,
    (status) => {
      if (status !== StatementStatus.FUNDED || !toValue(enabled)) return;
      queryClient.invalidateQueries({ queryKey: ['statements'] });
      queryClient.invalidateQueries({ queryKey: ['statement-files', toValue(id)] });
      queryClient.invalidateQueries({ queryKey: ['transfers', toValue(id)] });
    }
  );

  return query;
};

export const useDeleteStatement = () => {
  const queryClient = useQueryClient();
  const { filters } = useStatementsStore();

  return useMutation({
    mutationFn: (data) => api.deleteStatement(data.id),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['statements'] });
      queryClient.invalidateQueries({ queryKey: ['statement', variables.statement_id] });

      const statements = queryClient.getQueryData(['statements', filters]);
      if (statements?.data?.length === 0 && filters.page > 1) {
        filters.page = filters.page - 1;
      }
    },
  });
};

export const usePollStatementById = (id) => {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['statement', id],
    queryFn: () => api.getStatementById(toValue(id)),
    enabled: computed(() => !!toValue(id)),
    refetchInterval: ({ state }) => {
      if (state?.data?.data?.status === StatementStatus.REVIEWED) {
        return 5000;
      } else if (state?.data?.data?.status === StatementStatus.FUNDED) {
        return 10000;
      } else {
        return false;
      }
    },
  });

  watch(
    () => query.data.value?.data?.status,
    (newStatus, oldStatus) => {
      if (newStatus !== oldStatus) {
        queryClient.invalidateQueries({ queryKey: ['statements'] });
      }
    },
    { immediate: true }
  );

  return query;
};

export const useStatementImports = (id) => {
  return useQuery({
    queryKey: ['imports', id],
    queryFn: () => api.getStatementImports(toValue(id)),
  });
};

export const useStatementReservations = (id) => {
  const { filters } = useStatementReservationsStore();
  const searchParams = useSearchAwareParams({
    searchTerm: () => filters.search_text,
    defaultParams: filters,
    transformParams: {
      sort_by: 'relevance',
    },
  });

  return useQuery({
    queryKey: ['reservations', id, searchParams],
    queryFn: () => api.getStatementReservations(id, searchParams.value),
  });
};

export const useStatementTransfers = (id, poll = ref(false)) => {
  const { filters } = useStatementTransfersStore();
  const searchParams = useSearchAwareParams({
    searchTerm: () => filters.search_text,
    defaultParams: filters,
    transformParams: {
      sort_by: 'relevance',
    },
  });

  return useQuery({
    queryKey: ['transfers', id, searchParams],
    queryFn: () => api.getStatementTransfers(id, searchParams.value),
    retry: computed(() => (poll.value ? 0 : 3)),
    refetchInterval: computed(() => (poll.value ? 5000 : false)),
  });
};

export const useStatementFiles = (id, status) => {
  return useQuery({
    queryKey: ['statement-files', id],
    queryFn: () => api.getStatementFiles(toValue(id)),
    enabled: computed(() => status.value !== StatementStatus.OPENED),
    refetchInterval: ({ state }) => {
      const files = state.data?.data || [];
      const hasAnyProcessingFile = files.some((file) => file.status === StatementFileStatus.PROCESSING);
      return hasAnyProcessingFile ? 5000 : false;
    },
  });
};

export const useRequestStatementFile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, featureCategory }) => api.requestStatementFile(id, featureCategory),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['statement-files', variables.id] });
    },
  });
};

export const useCreateStatement = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => api.createStatement(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['statements'] });
    },
  });
};

export const useUpdateStatementStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => api.updateStatementStatus(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['statement', variables.statement_id] });
      queryClient.invalidateQueries({ queryKey: ['statement-files', variables.statement_id] });
      queryClient.invalidateQueries({ queryKey: ['statements'] });
      queryClient.invalidateQueries({ queryKey: ['transfers', variables.statement_id] });
      if (variables.event === StatementStatusTransitionEvents.REJECT) {
        queryClient.invalidateQueries({ queryKey: ['reservations'] });
      }
    },
  });
};
